import React, { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'

// ── Constants ────────────────────────────────────────────────────────────────

const COLORS = {
    facturado: '#378ADD',
    cobrado:   '#10B981',
    porCobrar: '#F59E0B',
    cerradoNC: '#DF3334',
}

const formatMXN = (val) =>
    new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
    }).format(parseFloat(val) || 0)

// ── Helpers ──────────────────────────────────────────────────────────────────

const toFloats = (arr = []) => arr.map(v => parseFloat(v) || 0)

const hasSomeNonZero = (arr = []) => arr.some(v => parseFloat(v) !== 0)

// ── Component ────────────────────────────────────────────────────────────────

/**
 * FlujoChart
 *
 * Renders the annual invoicing-flow chart with four semantically correct lines:
 *   1. Facturado  — all invoiced revenue (solid blue)
 *   2. Cobrado    — actually collected via complementos de pago (solid teal)
 *   3. Por cobrar — outstanding on active PPD invoices (dashed amber)
 *   4. Cerrado c/NC — closed via nota de crédito, never collected (dashed gray)
 *      Only rendered when at least one month has a non-zero value.
 *
 * Props:
 *   series     {Object}  { facturado[], cobrado[], porCobrar[], cerradoNC[] }
 *   categories {Array}   Month label strings e.g. ['Ene', 'Feb', …]
 */
const FlujoChart = ({ series: seriesData = {}, categories = [] }) => {
    const {
        facturado = [],
        cobrado   = [],
        porCobrar = [],
        cerradoNC = [],
    } = seriesData

    const showCerradoNC = useMemo(() => hasSomeNonZero(cerradoNC), [cerradoNC])

    const chartSeries = useMemo(() => {
        const s = [
            { name: 'Facturado',  data: toFloats(facturado) },
            { name: 'Cobrado',    data: toFloats(cobrado) },
            { name: 'Por cobrar', data: toFloats(porCobrar) },
        ]
        if (showCerradoNC) {
            s.push({ name: 'Cerrado c/NC', data: toFloats(cerradoNC) })
        }
        return s
    }, [facturado, cobrado, porCobrar, cerradoNC, showCerradoNC])

    const options = useMemo(() => {
        const colors    = showCerradoNC
            ? [COLORS.facturado, COLORS.cobrado, COLORS.porCobrar, COLORS.cerradoNC]
            : [COLORS.facturado, COLORS.cobrado, COLORS.porCobrar]
        const dashArray = showCerradoNC ? [0, 0, 6, 6] : [0, 0, 6]

        return {
            chart: {
                height: 350,
                type: 'line',
                toolbar: { show: true },
                zoom: { enabled: true },
            },
            dataLabels: { enabled: false },
            stroke: {
                width: 2,
                dashArray,
            },
            xaxis: { categories },
            colors,
            legend: { position: 'bottom' },
            fill: { opacity: 1 },
            tooltip: {
                y: {
                    formatter: formatMXN,
                },
            },
        }
    }, [categories, showCerradoNC])

    if (!categories.length) return null

    return (
        <ReactApexChart
            options={options}
            series={chartSeries}
            type="line"
            height="359"
        />
    )
}

export default FlujoChart
